const TYPE_GROUPS = [
  ['Advanced payment', 'Advanced Payment', ['advance payment']],
  ['Against Delivery', 'Material Delivery', ['material delivery']],
  ['Cable pulling', 'Cable Pulling', []],
  ['Installation', 'Installation', []],
  ['T&C', 'T&C', []],
  ['Handing Over', 'Handing Over', []],
  ['Retention', 'Retention', []],
];

const numeric = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const nameKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const groupKey = (name) => {
  const key = nameKey(name);
  const match = TYPE_GROUPS.find(([type, label, aliases]) => (
    [type, label, ...aliases].some((alias) => nameKey(alias) === key)
  ));
  return match ? match[0] : key || 'Unallocated';
};

export const selectPaymentScopes = (project, variations, invoices, variationInvoices, selection) => {
  const originalScopeValue = numeric(project?.original_scope_value ?? project?.total_project_value);
  const base = { value: originalScopeValue, terms: project?.payment_terms, invoices };
  const variationScopes = variations.map((variation) => ({
    id: variation.id,
    status: variation.status,
    value: numeric(variation.total_value ?? variation.financial_impact),
    terms: variation.payment_terms,
    invoices: variationInvoices.filter((invoice) => invoice.variation_id === variation.id),
  }));
  const approved = variationScopes.filter((variation) => variation.status === 'Approved');
  const approvedVariationsTotal = approved.reduce((sum, variation) => sum + variation.value, 0);
  const scopes = selection === 'base' ? [base]
    : selection === 'total' ? [base, ...approved]
      : variationScopes.filter((variation) => variation.id === selection);
  return {
    scopes,
    scopedInvoices: scopes.flatMap((scope) => scope.invoices),
    originalScopeValue,
    approvedVariationsTotal,
    computedTotalProjectValue: originalScopeValue + approvedVariationsTotal,
    scopeTotalValue: scopes.reduce((sum, scope) => sum + scope.value, 0),
  };
};

// Calculate each contract scope separately before combining it. A base invoice must
// never consume a variation's scheduled payment, even when their type names match.
export const summarizePaymentScopes = (scopes, drafts = {}) => {
  const totals = new Map(TYPE_GROUPS.map(([key, label]) => [key, {
    key, label, totalAmount: 0, invoiced: 0, received: 0, pending: 0, notInvoiced: 0,
  }]));
  for (const scope of scopes) {
    const groups = new Map();
    const ensureGroup = (name) => {
      const key = groupKey(name);
      if (!groups.has(key)) groups.set(key, {
        key, label: totals.get(key)?.label || String(name || 'Unallocated').trim(),
        planned: 0, invoiced: 0, received: 0, draftAmount: 0,
      });
      return groups.get(key);
    };
    const terms = Array.isArray(scope.terms) ? scope.terms.filter((term) => (
      term && typeof term === 'object' && String(term.name || '').trim()
    )) : [];
    for (const term of terms) {
      const target = numeric(term.target_amount);
      ensureGroup(term.name).planned += target > 0
        ? target : Math.max(0, numeric(term.percentage)) * scope.value / 100;
    }
    for (const invoice of scope.invoices) {
      const draft = drafts[invoice.id] || {};
      const amount = numeric(draft.amount ?? invoice.amount);
      const status = draft.status || invoice.status || 'Not Started';
      const linkedTerm = terms.find((term) => term.id && term.id === invoice.payment_term_id);
      const types = linkedTerm ? [linkedTerm.name] : [...new Set(
        String(draft.invoice_type ?? invoice.invoice_type ?? '').split(',').map((type) => type.trim()).filter(Boolean)
      )];
      if (!types.length) types.push('Unallocated');
      const percentages = draft.invoice_type_percentages ?? invoice.invoice_type_percentages;
      const validSplit = !linkedTerm && types.length > 1 && percentages
        && types.every((type) => Number.isFinite(Number(percentages[type])) && Number(percentages[type]) >= 0)
        && Math.abs(types.reduce((sum, type) => sum + Number(percentages[type]), 0) - 100) <= 0.01;
      for (const type of types) {
        const group = ensureGroup(type);
        const allocated = validSplit ? amount * Number(percentages[type]) / 100 : amount / types.length;
        if (status === 'Not Started') group.draftAmount += allocated;
        else group.invoiced += allocated;
        if (status === 'Payment Received') group.received += allocated;
      }
    }
    // Keep explicit draft invoices, but don't count them again on top of terms.
    const allocatedTotal = [...groups.values()].reduce((sum, group) => (
      sum + Math.max(group.planned, group.invoiced + group.draftAmount)
    ), 0);
    if (scope.value > allocatedTotal) {
      const unallocated = ensureGroup('Unallocated');
      unallocated.planned = Math.max(unallocated.planned, unallocated.invoiced + unallocated.draftAmount)
        + scope.value - allocatedTotal;
    }
    for (const group of groups.values()) {
      const totalAmount = Math.max(group.planned, group.invoiced + group.draftAmount);
      const result = totals.get(group.key) || {
        key: group.key, label: group.label, totalAmount: 0, invoiced: 0, received: 0, pending: 0, notInvoiced: 0,
      };
      result.totalAmount += totalAmount;
      result.invoiced += group.invoiced;
      result.received += group.received;
      result.pending += Math.max(0, group.invoiced - group.received);
      result.notInvoiced += Math.max(0, totalAmount - group.invoiced);
      totals.set(group.key, result);
    }
  }
  const byType = [...totals.values()];
  return {
    byType,
    totalInvoiced: byType.reduce((sum, group) => sum + group.invoiced, 0),
    totalReceived: byType.reduce((sum, group) => sum + group.received, 0),
    totalPending: byType.reduce((sum, group) => sum + group.pending, 0),
    totalNotInvoiced: byType.reduce((sum, group) => sum + group.notInvoiced, 0),
  };
};

// ------------------------------------------------------------------ invoices per payment term (Variations 11 #3)
//
// Term names are typed by hand ("Against Delivery (30 Days PDC)", "50% Advance  Payment", "Against 120 Days Due
// T&C", "ADVANE PAYMENT"), while invoice types come from the fixed list above. Most variation invoices carry no
// payment_term_id, so an invoice is matched to a term by: its payment_term_id; else its type against the term's
// type — the same groupKey as summarizePaymentScopes, then key words of the term name; else, when the VO has a
// single term, that term. Anything left over is reported as unlinked, never as "not invoiced".

const TERM_KEYWORDS = [
  ['Retention', /retention/],
  ['T&C', /\bt\s*&\s*c\b|testing|commissioning/],
  ['Handing Over', /hand(ing)?\s*-?\s*over|compl[ae]tion/],
  ['Cable pulling', /cable/],
  ['Installation', /install/],
  ['Against Delivery', /deliver/],
  ['Advanced payment', /\badva|down\s*payment|mobili[sz]ation/],
];
const KNOWN_GROUPS = new Set(TYPE_GROUPS.map(([key]) => key));

/** The invoice type a payment term (or an invoice type) stands for: "Against T & C" → "T&C"; null if unknown. */
export const paymentTypeOf = (name) => {
  const key = groupKey(name);
  if (KNOWN_GROUPS.has(key)) return key;
  const text = nameKey(name);
  if (!text) return null;
  const match = TERM_KEYWORDS.find(([, pattern]) => pattern.test(text));
  return match ? match[0] : null;
};

const INVOICE_PROGRESS = { 'Not Started': 0, Submitted: 1, 'Payment Received': 2 };

/** One status for the invoices of a term: "Payment Received" only when every one is; else the least advanced. */
export const combinedInvoiceStatus = (invoices) => {
  const statuses = invoices.map((invoice) => invoice.status || 'Not Started');
  if (!statuses.length) return null;
  return statuses.reduce((least, status) => (
    (INVOICE_PROGRESS[status] ?? 0) < (INVOICE_PROGRESS[least] ?? 0) ? status : least
  ));
};

/**
 * Splits a VO's invoices over its payment terms.
 * → { byTerm: [{ term, invoices, invoiced, received, status }], unlinked: [invoice] } (byTerm in the terms' order)
 */
export const matchInvoicesToTerms = (terms, invoices) => {
  const list = (Array.isArray(terms) ? terms : []).filter((term) => term && typeof term === 'object');
  const types = list.map((term) => paymentTypeOf(term.name));
  const buckets = list.map(() => []);
  const unlinked = [];
  for (const invoice of Array.isArray(invoices) ? invoices : []) {
    const byId = invoice.payment_term_id ? list.findIndex((term) => term.id && term.id === invoice.payment_term_id) : -1;
    if (byId >= 0) {
      buckets[byId].push(invoice);
      continue;
    }
    const invoiceTypes = [...new Set(String(invoice.invoice_type || '').split(',').map(paymentTypeOf).filter(Boolean))];
    // A type two terms share ("Delivery phase 1 / 2") says nothing about which one: leave it unlinked.
    const targets = invoiceTypes
      .map((type) => types.reduce((found, termType, index) => (termType === type ? [...found, index] : found), []))
      .filter((indexes) => indexes.length === 1)
      .map(([index]) => index);
    if (targets.length) [...new Set(targets)].forEach((index) => buckets[index].push(invoice));
    else if (list.length === 1) buckets[0].push(invoice); // older invoices of a one-term VO have no term at all
    else unlinked.push(invoice);
  }
  return {
    byTerm: list.map((term, index) => {
      const matched = buckets[index];
      return {
        term,
        invoices: matched,
        invoiced: matched.filter((invoice) => (invoice.status || 'Not Started') !== 'Not Started').reduce((sum, invoice) => sum + numeric(invoice.amount), 0),
        received: matched.filter((invoice) => invoice.status === 'Payment Received').reduce((sum, invoice) => sum + numeric(invoice.amount), 0),
        status: combinedInvoiceStatus(matched),
      };
    }),
    unlinked,
  };
};
